import { useEffect, useRef, useState } from 'react'
import { Button, Checkbox, WorkflowMark } from '@workflow/ui'
import { TEAM_TERMS_VERSION } from '@workflow/contracts'
import { useAcceptTeamTerms } from '@/lib/api/hooks'
import { errorSentence } from '@/lib/api/client'
import { GateFrame } from '@/shell/GateFrame'
import { TEAM_TERMS_DOCUMENT } from './teamTermsDocument'

function renderDocument(source: string) {
  return source.split(/\n\s*\n/).map((block, index) => {
    const text = block.trim()
    if (!text) return null
    const heading = /^(#{1,2})\s+(.+)$/.exec(text)
    if (heading) {
      const title = heading[2]!.replace(/\*\*/g, '')
      return heading[1]!.length === 1
        ? <h2 key={index} className="wf-heading text-foreground">{title}</h2>
        : <h3 key={index} className="mt-7 text-ui font-semibold text-foreground">{title}</h3>
    }
    return <p key={index} className="whitespace-pre-wrap text-ui leading-relaxed text-muted-foreground">{text.replace(/\*\*/g, '')}</p>
  })
}

/** One-time, account-bound acceptance before a staff/player account can proceed. */
export function TermsAcceptanceScreen({ email, issuedOn }: { email: string; issuedOn: string }) {
  const reader = useRef<HTMLDivElement>(null)
  const [readToEnd, setReadToEnd] = useState(false)
  const [agreed, setAgreed] = useState(false)
  const accept = useAcceptTeamTerms()

  useEffect(() => {
    const element = reader.current
    if (element && element.scrollHeight <= element.clientHeight + 4) setReadToEnd(true)
  }, [])

  const onReadScroll = () => {
    const element = reader.current
    if (element && element.scrollTop + element.clientHeight >= element.scrollHeight - 12) setReadToEnd(true)
  }

  const submit = () => {
    if (!readToEnd || !agreed) return
    accept.mutate({ version: TEAM_TERMS_VERSION, agree: true })
  }

  return (
    <div className="dark">
      <GateFrame width="max-w-[54rem]">
        <div className="mb-5 flex items-center gap-2 text-muted-foreground">
          <WorkflowMark size={15} className="text-primary" />
          <span className="text-micro uppercase tracking-[0.18em]">Team access</span>
          <span className="ml-auto text-micro">{email}</span>
        </div>

        <div className="rounded-2xl border border-hairline bg-surface shadow-lg">
          <header className="flex flex-wrap items-end justify-between gap-3 border-b border-hairline px-6 py-5">
            <div>
              <p className="text-micro uppercase tracking-[0.18em] text-muted-foreground">Required before continuing</p>
              <h1 className="mt-1 wf-heading text-foreground">Team Terms and Conditions</h1>
              <p className="mt-1 text-caption text-muted-foreground">Version {TEAM_TERMS_VERSION} · issued {issuedOn}</p>
            </div>
            <span className="rounded-full border border-hairline px-3 py-1 text-micro text-muted-foreground">One-time acceptance</span>
          </header>

          <div
            ref={reader}
            role="region"
            aria-label="Team Terms and Conditions. Scroll to the end to continue."
            tabIndex={0}
            onScroll={onReadScroll}
            onCopy={(event) => event.preventDefault()}
            onCut={(event) => event.preventDefault()}
            onContextMenu={(event) => event.preventDefault()}
            onKeyDown={(event) => {
              if ((event.ctrlKey || event.metaKey) && ['a', 'c', 'x'].includes(event.key.toLowerCase())) event.preventDefault()
            }}
            className="h-[min(56vh,640px)] min-h-[300px] overflow-y-auto overscroll-contain px-6 py-6 [user-select:none]"
          >
            <div className="space-y-4">{renderDocument(TEAM_TERMS_DOCUMENT)}</div>
          </div>

          <div className="border-t border-hairline px-6 py-5">
            <p className="mb-3 text-micro text-muted-foreground">
              {readToEnd ? 'You reached the end of the document.' : 'Scroll through the complete document to enable acceptance.'}
            </p>
            <Checkbox checked={agreed} disabled={!readToEnd} onChange={setAgreed}>
              I have read and agree to Thunder&apos;s Lab Studio&apos;s Team Terms and Conditions, including the confidentiality and information security obligations.
            </Checkbox>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <Button variant="primary" size="md" busy={accept.isPending} disabled={!readToEnd || !agreed} onClick={submit}>
                Accept and continue
              </Button>
              {accept.error ? <p role="alert" className="text-caption text-danger">{errorSentence(accept.error)}</p> : null}
            </div>
            <p className="mt-4 text-micro leading-relaxed text-muted-foreground/80">
              Acceptance is recorded against this signed-in account and version. The reader discourages copying; screenshots and browser inspection remain possible.
            </p>
          </div>
        </div>
      </GateFrame>
    </div>
  )
}
